import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { site } from '@shared/site.config';
import DimensionLine from '@/components/DimensionLine';
import BuildingGlyph from '@/components/BuildingGlyph';
import type { GlyphType } from '@/components/BuildingGlyph';
import type { LeadInterest } from '@shared/types';

/**
 * Shared layout for the city + service "local sheets" (S-01…S-05) — a sibling
 * series to ServiceSheet's A-01…A-03, built for a narrower, higher-intent
 * search than "commercial real estate Waterloo Region." Two things ServiceSheet
 * doesn't need but every one of these does: a split for the two audiences one
 * search term actually contains (the tenant looking, the landlord with space
 * to fill), and an explicit, honest answer to "where's the listings page" —
 * because there isn't one, on purpose, and that's worth saying rather than
 * leaving a visitor to wonder why.
 */

interface Audience {
  label: string;
  body: string;
  ctaLabel: string;
}

export interface Faq {
  q: string;
  a: string;
}

interface RelatedLink {
  to: string;
  label: string;
}

interface Props {
  sheetNo: string;
  kicker: string;
  h1: ReactNode;
  intro: string;
  glyph: GlyphType;
  audiences: [Audience, Audience];
  localLabel: string;
  localBody: ReactNode;
  honestLabel: string;
  honestBody: string;
  faqs: Faq[];
  interest: LeadInterest;
  closingLine: string;
  related: RelatedLink[];
}

export default function LocalPageSheet({
  sheetNo,
  kicker,
  h1,
  intro,
  glyph,
  audiences,
  localLabel,
  localBody,
  honestLabel,
  honestBody,
  faqs,
  interest,
  closingLine,
  related,
}: Props) {
  const tel = `tel:${site.agent.phone.replace(/\D/g, '')}`;
  const contactHref = `/contact?interest=${interest}`;

  return (
    <>
      {/* Sheet head */}
      <section className="relative border-b border-medline bg-grid-sheet">
        <span className="sheet-no absolute right-5 top-5 sm:right-8">{sheetNo}</span>

        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_1fr]">
            <div className="animate-fade-in">
              <p className="annot">{kicker}</p>
              <h1 className="mt-4 font-display text-[36px] font-semibold leading-[1.05] tracking-tight sm:text-[52px]">
                {h1}
              </h1>
              <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-annot">{intro}</p>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to={contactHref} className="btn-primary">Start a conversation</Link>
                <a href={tel} className="btn-ghost">Call {site.agent.phone}</a>
              </div>
            </div>

            <div className="hidden lg:block">
              <BuildingGlyph type={glyph} className="w-full text-medline" />
              <p className="annot mt-5 text-right">Fig. 1 — Elevation, typical</p>
            </div>
          </div>
        </div>
      </section>

      {/* Two audiences one search term actually contains */}
      <section className="bg-sheet">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <DimensionLine label="Two ways in" className="mb-12" />

          <div className="grid gap-px border border-medline bg-medline sm:grid-cols-2">
            {audiences.map((a) => (
              <div key={a.label} className="bg-sheet px-7 py-8">
                <p className="annot">{a.label}</p>
                <p className="mt-3 text-[15px] leading-relaxed text-graphite-600">{a.body}</p>
                <Link
                  to={contactHref}
                  className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider2 text-redline hover:text-redline-600"
                >
                  {a.ctaLabel}
                  <svg width="18" height="8" viewBox="0 0 18 8" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M0 4h16M13 1l3 3-3 3" />
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* This market, and the honest answer on why there's no listings page */}
      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <DimensionLine label="This market" className="mb-12" />

          <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr]">
            <div className="max-w-2xl">
              <p className="annot">{localLabel}</p>
              <div className="mt-3 space-y-4 text-[16px] leading-relaxed text-graphite-600">{localBody}</div>
            </div>

            <div className="h-fit border-l-2 border-redline bg-redline-100/50 px-6 py-5">
              <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">{honestLabel}</p>
              <p className="mt-2 text-[14px] leading-relaxed text-graphite-600">{honestBody}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — also the source for this page's FAQPage structured data */}
      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <DimensionLine label="Questions" className="mb-12" />

          <div className="max-w-4xl">
            {faqs.map((f, i) => (
              <div
                key={f.q}
                className="grid grid-cols-[auto_1fr] items-start gap-6 border-t border-hairline py-6 last:border-b last:border-hairline"
              >
                <span className="sheet-no pt-1.5">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h2 className="font-display text-[18px] leading-snug">{f.q}</h2>
                  <p className="mt-2 text-[15px] leading-relaxed text-graphite-600">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related sheets, then closing */}
      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
          {related.length > 0 && (
            <div className="mb-12 flex flex-wrap items-baseline gap-x-8 gap-y-3 border-b border-hairline pb-10">
              <span className="annot shrink-0">Also see</span>
              {related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="text-[14px] text-redline hover:text-redline-600"
                >
                  {r.label}
                </Link>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-end justify-between gap-8">
            <h2 className="max-w-xl font-display text-[28px] leading-tight sm:text-[34px]">{closingLine}</h2>
            <div className="flex flex-wrap gap-3">
              <Link to={contactHref} className="btn-primary">Start a conversation</Link>
              <Link to="/about" className="btn-ghost">About Pinnacle Commercial</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
